/**
 * Tadika "Tarik Garisan" games: drag a line from one side to its match.
 * Four kinds: colour swatch to colour name, small letter to capital letter, animal halves joined, job picture to job name.
 * Bahasa Melayu for BM, English for every other app language. Every spoken word already has a clip (category names, colour names, "Letter X.").
 */
import { kategoriFor } from "./tadikaEn.js";
import { warnaFor } from "./tadikaGamesEn.js";

export type GarisMode = "warna" | "huruf" | "haiwan" | "pekerjaan" | "kenderaan" | "buah";
export const GARIS_MODES: readonly GarisMode[] = ["warna", "huruf", "haiwan", "pekerjaan", "kenderaan", "buah"];
export const GARIS_PAIRS = 4;
export const GARIS_PAGES = 2;

export type GarisPair = {
  /** Same key on both sides = a correct match (stable across languages). */
  key: string;
  /** What the left node shows: a colour hex, a small letter or a picture id. */
  left: string;
  /** The word on the right node (colour name, capital letter, animal or job name in the content language). */
  right: string;
  /** Picture id for haiwan / pekerjaan. */
  art?: string;
};

const shuffled = <T,>(a: readonly T[], rnd: () => number): T[] => {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [b[i], b[j]] = [b[j]!, b[i]!]; }
  return b;
};

/** The whole pool a mode draws from, in the content language. */
export function garisPool(mode: GarisMode, lang: "ms" | "en"): GarisPair[] {
  if (mode === "warna") {
    const ms = warnaFor("ms"); const loc = warnaFor(lang);
    return ms.flatMap((w, i) => (w.name === "biru muda" ? [] : [{ key: w.name, left: w.hex, right: loc[i]!.name }]));
  }
  if (mode === "huruf") return "abcdefghijklmnopqrstuvwxyz".split("").map((l) => ({ key: l, left: l, right: l.toUpperCase() }));
  const cat = kategoriFor(lang).find((c) => c.id === mode)!;
  return cat.items.map((i) => ({ key: i.id, left: i.art ?? `${mode}/${i.id}`, right: i.ms, art: i.art ?? `${mode}/${i.id}` }));
}

/** GARIS_PAGES pages of GARIS_PAIRS different pairs each. */
export function garisPages(mode: GarisMode, lang: "ms" | "en", rnd: () => number = Math.random): GarisPair[][] {
  const pick = shuffled(garisPool(mode, lang), rnd).slice(0, GARIS_PAIRS * GARIS_PAGES);
  return Array.from({ length: GARIS_PAGES }, (_, p) => pick.slice(p * GARIS_PAIRS, (p + 1) * GARIS_PAIRS));
}

/** Every English/Malay word this game speaks, so the audio test can check a clip exists. */
export function garisTexts(lang: "ms" | "en"): { kind: "word" | "line"; text: string }[] {
  const out: { kind: "word" | "line"; text: string }[] = [];
  for (const m of ["warna", "haiwan", "pekerjaan", "kenderaan", "buah"] as const) for (const p of garisPool(m, lang)) out.push({ kind: "word", text: p.right });
  if (lang === "en") for (const p of garisPool("huruf", "en")) out.push({ kind: "line", text: `Letter ${p.right}.` });
  return out;
}
